import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Paper, Alert, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

// Custom CSS for state popups
const popupStyle = `
  .state-popup .leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border: none;
    padding: 0;
  }
  .state-popup .leaflet-popup-content {
    margin: 0;
    padding: 0;
  }
  .state-tooltip {
    background: rgba(30, 64, 175, 0.9);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 500;
  }
`;

// Inject custom styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = popupStyle;
  if (!document.head.querySelector('style[data-state-popup]')) {
    styleElement.setAttribute('data-state-popup', 'true');
    document.head.appendChild(styleElement);
  }
}

// Fix for default marker icons in Leaflet with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: number[][][] | number[][][][];
  };
  properties?: Record<string, unknown>;
}

interface GeoJSONData {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

// Component to handle map click and add marker
const MapClickHandler: React.FC<{
  onMapClick: (lat: number, lng: number) => void;
  testCoordinates: { lat: number; lng: number } | null;
  isInUSA: boolean | null;
  viewMode: 'country' | 'states';
}> = ({ onMapClick, testCoordinates, isInUSA, viewMode }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e: L.LeafletMouseEvent) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);

  // Center map on test coordinates if provided
  useEffect(() => {
    if (testCoordinates) {
      map.setView([testCoordinates.lat, testCoordinates.lng], 10);
    }
  }, [testCoordinates, map]);

  // Adjust view when switching modes
  useEffect(() => {
    if (viewMode === 'states') {
      map.setView([39.8283, -98.5795], 5);
    } else {
      map.setView([39.8283, -98.5795], 4);
    }
  }, [viewMode, map]);

  return null;
};

const CountryBoundaries: React.FC = () => {
  const [viewMode, setViewMode] = useState<'country' | 'states'>('country');
  const [geojsonData, setGeojsonData] = useState<GeoJSONData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testLat, setTestLat] = useState<string>('40.7128');
  const [testLng, setTestLng] = useState<string>('-74.0060');
  const [testCoordinates, setTestCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isInUSA, setIsInUSA] = useState<boolean | null>(null);
  const [stateName, setStateName] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Load GeoJSON data based on view mode
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
          ? process.env.REACT_APP_REMOTE_API_URL
          : process.env.REACT_APP_LOCAL_API_URL;

        const endpoint = viewMode === 'country' 
          ? '/api/admin/geojson/usa-boundaries'
          : '/api/admin/geojson/usa-states';
        
        const response = await axios.get<GeoJSONData>(
          `${apiBaseUrl}${endpoint}`,
          {
            withCredentials: true, // Required to send HttpOnly cookies
          }
        );

        setGeojsonData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load GeoJSON data');
        console.error('Error loading GeoJSON:', err);
      } finally {
        setLoading(false);
      }
    };

    loadGeoJSON();
  }, [viewMode]);

  // Check if coordinates are in USA or state
  const checkCoordinates = async (lat: number, lng: number) => {
    try {
      setChecking(true);
      setTestCoordinates({ lat, lng });
      setError(null);
      setStateName(null);

      const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
        ? process.env.REACT_APP_REMOTE_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      // Call appropriate backend test endpoint
      const endpoint = viewMode === 'country'
        ? '/api/admin/geojson/test-coordinates'
        : '/api/admin/geojson/test-state-coordinates';
      
      const response = await axios.get(
        `${apiBaseUrl}${endpoint}`,
        {
          params: { latitude: lat, longitude: lng },
          withCredentials: true, // Required to send HttpOnly cookies
        }
      );

      setIsInUSA(response.data.isInUnitedStates || false);
      if (viewMode === 'states' && response.data.state) {
        setStateName(response.data.state);
      }
    } catch (err: any) {
      console.error('Error checking coordinates:', err);
      setError(err.response?.data?.message || 'Failed to check coordinates');
      setIsInUSA(null);
      setStateName(null);
    } finally {
      setChecking(false);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setTestLat(lat.toFixed(6));
    setTestLng(lng.toFixed(6));
    checkCoordinates(lat, lng);
  };

  const handleTestClick = () => {
    const lat = parseFloat(testLat);
    const lng = parseFloat(testLng);

    if (isNaN(lat) || isNaN(lng)) {
      setError('Invalid coordinates');
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('Coordinates out of range');
      return;
    }

    setError(null);
    checkCoordinates(lat, lng);
  };

  // Style for GeoJSON - different colors for country vs states
  const geoJSONStyle = (feature?: any) => {
    if (viewMode === 'states') {
      return {
        fillColor: '#60a5fa',
        weight: 1.5,
        opacity: 1,
        color: '#2563eb',
        dashArray: '',
        fillOpacity: 0.4,
      };
    }
    return {
      fillColor: '#3b82f6',
      weight: 2,
      opacity: 1,
      color: '#1e40af',
      dashArray: '',
      fillOpacity: 0.3,
    };
  };

  // Handle feature hover and click for states
  const onEachFeature = (feature: any, layer: L.Layer) => {
    if (viewMode === 'states' && feature.properties) {
      const stateName = 
        feature.properties.name || 
        feature.properties.NAME || 
        feature.properties.name_en || 
        feature.properties.NAME_EN || 
        'Unknown State';
      
      // Add tooltip on hover (shows state name)
      layer.bindTooltip(stateName, {
        permanent: false,
        direction: 'center',
        className: 'state-tooltip',
        offset: [0, 0],
        interactive: false
      });

      // Make states clickable to test coordinates at click location
      // Note: The map click handler will also catch this, but this ensures
      // the state layer doesn't block the click event
      layer.on('click', (e: L.LeafletMouseEvent) => {
        e.originalEvent.stopPropagation(); // Prevent double-trigger
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        setTestLat(lat.toFixed(6));
        setTestLng(lng.toFixed(6));
        checkCoordinates(lat, lng);
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          {viewMode === 'country' ? 'USA Country Boundaries' : 'USA State Boundaries'}
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
              setTestCoordinates(null);
              setIsInUSA(null);
              setStateName(null);
            }
          }}
          aria-label="view mode"
        >
          <ToggleButton value="country" aria-label="country">
            Country
          </ToggleButton>
          <ToggleButton value="states" aria-label="states">
            States
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visualize {viewMode === 'country' ? 'USA boundaries' : 'USA state boundaries'} from Natural Earth GeoJSON data. Click on the map or enter coordinates to test.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Latitude"
            type="number"
            value={testLat}
            onChange={(e) => setTestLat(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          />
          <TextField
            label="Longitude"
            type="number"
            value={testLng}
            onChange={(e) => setTestLng(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          />
          <Button
            variant="contained"
            onClick={handleTestClick}
            disabled={checking}
          >
            {checking ? 'Checking...' : 'Test Coordinates'}
          </Button>
          {isInUSA !== null && (
            <Alert
              severity={isInUSA ? 'success' : 'warning'}
              sx={{ flexGrow: 1 }}
            >
              {isInUSA
                ? `✅ Location is in the United States${stateName ? ` (${stateName})` : ''}`
                : '❌ Location is outside the United States'}
            </Alert>
          )}
        </Box>
      </Paper>

      <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 600 }}>
        <MapContainer
          center={[39.8283, -98.5795]} // Center of USA
          zoom={viewMode === 'states' ? 5 : 4} // Slightly zoomed in for states
          style={{ height: '100%', width: '100%', zIndex: 0 }}
          key={viewMode} // Force remount when view mode changes to update zoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {geojsonData && (
            <GeoJSON
              data={geojsonData as any}
              style={geoJSONStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {testCoordinates && (
            <Marker
              position={[testCoordinates.lat, testCoordinates.lng]}
              icon={L.icon({
                iconUrl: isInUSA
                  ? 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png'
                  : 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
              })}
            >
              <Popup>
                <div>
                  <strong>Test Location</strong>
                  <br />
                  Lat: {testCoordinates.lat.toFixed(6)}
                  <br />
                  Lng: {testCoordinates.lng.toFixed(6)}
                  <br />
                  {isInUSA !== null && (
                    <>
                      <strong>
                        {isInUSA ? '✅ In USA' : '❌ Outside USA'}
                      </strong>
                      {stateName && (
                        <>
                          <br />
                          State: <strong>{stateName}</strong>
                        </>
                      )}
                    </>
                  )}
                </div>
              </Popup>
            </Marker>
          )}

          <MapClickHandler
            onMapClick={handleMapClick}
            testCoordinates={testCoordinates}
            isInUSA={isInUSA}
            viewMode={viewMode}
          />
        </MapContainer>
      </Box>

      {geojsonData && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>GeoJSON Info:</strong> {geojsonData.features.length} {viewMode === 'states' ? 'state(s)' : 'feature(s)'} loaded
            {viewMode === 'states' && geojsonData.features.length > 0 && (
              <>
                <br />
                <strong>States:</strong> {geojsonData.features
                  .map((f: any) => f.properties?.name || f.properties?.NAME || f.properties?.name_en || f.properties?.NAME_EN || 'Unknown')
                  .filter((name: string) => name !== 'Unknown')
                  .slice(0, 10)
                  .join(', ')}
                {geojsonData.features.length > 10 && ` ... and ${geojsonData.features.length - 10} more`}
              </>
            )}
            {viewMode === 'country' && geojsonData.features[0]?.properties && (
              <>
                <br />
                Country: {geojsonData.features[0].properties.NAME || geojsonData.features[0].properties.NAME_LONG || 'N/A'}
              </>
            )}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CountryBoundaries;
